import React from 'react';
import { Form, Input, Button, Alert, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';

const { Title, Text } = Typography;

const LoginForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (values) => {
    clearError();
    const result = await login(values);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handleFormChange = () => {
    if (error) {
      clearError();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
      <div className="flex min-h-screen w-full">
        {/* Left Column - Logo and Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black bg-opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
            {/* Logo */}
            <div className="mb-12">
              <img 
                src="/qqcrm-logo.png" 
                alt="QQCRM Logo" 
                className="h-16 xl:h-20 w-auto filter brightness-0 invert"
              />
            </div>
            
            {/* Branding Content */}
            <div className="text-center text-white max-w-md">
              <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                Welcome back to QQCRM
              </h1>
              <p className="text-lg xl:text-xl text-blue-100 mb-10 leading-relaxed">
                Continue managing your sales pipeline and growing your business with our powerful CRM platform.
              </p>
              
              {/* Features */}
              <div className="space-y-5 text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Secure & Reliable Access</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Real-time Data Sync</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Multi-device Support</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">24/7 Customer Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Login Form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center lg:text-left">
              {/* Mobile Logo */}
              <div className="lg:hidden mb-6">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-12 w-auto mx-auto"
                />
              </div>
              
              <Title level={2} className="text-gray-900 dark:text-gray-100 mb-2">
                Welcome back
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 text-lg">
                Sign in to your CRM account
              </Text>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full flex items-center justify-center">
            <div className="w-full max-w-md">
              {error && (
                <Alert
                  message="Login Failed"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={clearError}
                  className="mb-6"
                  role="alert"
                  aria-live="polite"
                />
              )}

              <Form
                form={form}
                name="login"
                onFinish={handleSubmit}
                onValuesChange={handleFormChange}
                layout="vertical"
                size="large"
                autoComplete="on"
              >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input
            prefix={<UserOutlined className="text-gray-400" />}
            placeholder="Enter your email"
            autoComplete="email"
            className="focus-visible:focus"
            aria-describedby="email-error"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: 'Please enter your password' }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter your password"
            autoComplete="current-password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            className="focus-visible:focus"
            aria-describedby="password-error"
          />
        </Form.Item>

        <Form.Item className="mb-4">
          <div className="flex justify-between items-center">
            <Link 
              to="/forgot-password" 
              className="text-primary-600 hover:text-primary-500 text-sm focus-visible:focus"
            >
              Forgot password?
            </Link>
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            className="w-full h-12 text-base font-medium touch-target focus-visible:focus"
            aria-describedby={error ? "login-error" : undefined}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
              </Form.Item>
              </Form>

              <Divider className="my-6">
                <Text className="text-gray-500 dark:text-gray-400 text-sm">
                  New to QQCRM?
                </Text>
              </Divider>

              <div className="text-center lg:text-left">
                <Link 
                  to="/auth/register" 
                  className="text-primary-600 hover:text-primary-500 font-medium focus-visible:focus"
                >
                  Create your account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
